import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Brain, Target, Sparkles, Smile } from 'lucide-react';
import { ActivityReport } from './ActivityReport';

interface QuickFixProps {
  onClose: () => void;
  initialStressLevel: number;
}

interface Game {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

const games: Game[] = [
  {
    id: 'follow-dot',
    name: 'Follow the Dot',
    description: 'Follow the floating dot with your eyes. Take slow, deep breaths.',
    icon: <Target className="w-5 h-5" />,
    color: 'from-primary to-purple-500'
  },
  {
    id: 'color-breathe',
    name: 'Color Breathing',
    description: 'Watch the colors shift as you breathe. Match your breath to the rhythm.',
    icon: <Sparkles className="w-5 h-5" />,
    color: 'from-purple-500 to-blue-500'
  },
  {
    id: 'find-calm',
    name: 'Find Your Calm',
    description: 'Tap the calming circles as they appear. Each tap brings more peace.',
    icon: <Smile className="w-5 h-5" />,
    color: 'from-blue-500 to-cyan-500'
  }
];

export function QuickFix({ onClose, initialStressLevel }: QuickFixProps) {
  const [selectedGame, setSelectedGame] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [dotPosition, setDotPosition] = useState({ x: 50, y: 50 });
  const [breathePhase, setBreathePhase] = useState<'inhale' | 'hold' | 'exhale'>('inhale');
  const [calmCircles, setCalmCircles] = useState<Array<{ id: number; x: number; y: number; active: boolean }>>(
    Array.from({ length: 9 }, (_, i) => ({
      id: i,
      x: Math.random() * 80 + 10,
      y: Math.random() * 80 + 10,
      active: true
    }))
  );

  useEffect(() => {
    if (isPlaying && selectedGame === 'follow-dot') {
      const interval = setInterval(() => {
        setDotPosition({
          x: 20 + Math.random() * 60,
          y: 20 + Math.random() * 60
        });
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isPlaying, selectedGame]);

  useEffect(() => {
    if (isPlaying && selectedGame === 'color-breathe') {
      const interval = setInterval(() => {
        setBreathePhase(prev => {
          switch (prev) {
            case 'inhale': return 'hold';
            case 'hold': return 'exhale';
            case 'exhale': return 'inhale';
          }
        });
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [isPlaying, selectedGame]);

  const handleCalmCircleClick = (id: number) => {
    setCalmCircles(prev => 
      prev.map(circle => 
        circle.id === id ? { ...circle, active: false } : circle
      )
    );

    const remainingActive = calmCircles.filter(circle => circle.id !== id && circle.active).length;
    if (remainingActive === 0) {
      setShowReport(true);
      setIsPlaying(false);
    }
  };

  const handleEndGame = () => {
    setShowReport(true);
    setIsPlaying(false);
  };

  if (showReport) {
    return (
      <ActivityReport
        initialStressLevel={initialStressLevel}
        duration={5}
        activityType={selectedGame ? games.find(g => g.id === selectedGame)?.name || '' : ''}
        onClose={onClose}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-xl z-[100] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-background/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold">Quick Fix</h2>
        </div>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-3">
        <div className="max-w-2xl mx-auto">
          {/* Game Area */}
          <div className="aspect-video bg-gradient-to-br from-background to-muted rounded-lg overflow-hidden relative mb-3">
            {selectedGame && (
              <div className={`absolute inset-0 bg-gradient-to-br ${
                games.find(g => g.id === selectedGame)?.color
              } opacity-5`} />
            )}
            
            {isPlaying ? (
              <>
                {selectedGame === 'follow-dot' && (
                  <div 
                    className="absolute w-6 h-6 rounded-full bg-gradient-to-br from-primary to-purple-500"
                    style={{ 
                      left: `${dotPosition.x}%`,
                      top: `${dotPosition.y}%`,
                      transition: 'all 3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 0 20px rgba(var(--primary), 0.5)'
                    }}
                  />
                )}
                
                {selectedGame === 'color-breathe' && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div 
                      className={`w-48 h-48 rounded-full bg-gradient-to-br from-primary to-purple-500 transition-all duration-1000`}
                      style={{
                        transform: breathePhase === 'inhale' ? 'scale(1.2)' : 
                                 breathePhase === 'hold' ? 'scale(1)' : 'scale(0.8)',
                        opacity: breathePhase === 'hold' ? 0.8 : 0.5
                      }}
                    />
                  </div>
                )}

                {selectedGame === 'find-calm' && (
                  <div className="absolute inset-0">
                    <AnimatePresence>
                      {calmCircles.map(circle => (
                        circle.active && (
                          <motion.button
                            key={circle.id}
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0, opacity: 0 }}
                            className="absolute w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-purple-500/20 hover:from-primary/30 hover:to-purple-500/30 transition-all duration-500"
                            style={{
                              left: `${circle.x}%`,
                              top: `${circle.y}%`,
                              animation: 'pulse 2s infinite'
                            }}
                            onClick={() => handleCalmCircleClick(circle.id)}
                          />
                        )
                      ))}
                    </AnimatePresence>
                  </div>
                )}

                <button
                  onClick={handleEndGame}
                  className="absolute bottom-3 right-3 px-3 py-1.5 rounded-lg bg-muted/50 text-xs backdrop-blur-sm"
                >
                  End Game
                </button>
              </>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center p-3">
                {selectedGame ? (
                  <>
                    <h3 className="text-xl font-semibold mindful-gradient-text mb-3">
                      {games.find(g => g.id === selectedGame)?.name}
                    </h3>
                    <p className="text-sm text-muted-foreground max-w-md mb-4">
                      {games.find(g => g.id === selectedGame)?.description}
                    </p>
                    <button
                      onClick={() => setIsPlaying(true)}
                      className="mindful-timer px-4 py-2 rounded-lg text-primary font-medium transition-all hover:bg-primary/20"
                    >
                      Begin
                    </button>
                  </>
                ) : (
                  <div className="grid grid-cols-3 gap-3 w-full max-w-lg px-3">
                    {games.map(game => (
                      <motion.button
                        key={game.id}
                        onClick={() => setSelectedGame(game.id)}
                        className="mindful-timer p-4 rounded-lg text-primary relative group overflow-hidden"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <div className="relative flex flex-col items-center gap-2">
                          {game.icon}
                          <div className="text-center">
                            <div className="text-sm font-medium mb-1">{game.name}</div>
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              {game.description}
                            </p>
                          </div>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}